import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { MultipleUserInput } from '@/components/MultipleUserInput';
import { RevealSlot } from '@/components/RevealSlot';
import { Slogan } from '@/components/Slogan';
import {
    EUserFlow,
    selectIsAssetTypeKnownViaParams,
    selectIsVehicleKnown,
} from '@/context/borrower';
import {
    EDisplays,
    selectHideVehicleScreen,
    selectIsAssetKnownViaParams,
    selectPartner,
    selectPreviousDisplay,
} from '@/context/display';
import { useAppSelector } from '@/context/storeHooks';
import { Balloon } from '@/UserInput/Balloon';
import { BusinessFinanceType } from '@/UserInput/BusinessFinanceType';
import { Deposit } from '@/UserInput/Deposit';
import { LoanTerm } from '@/UserInput/LoanTerm';
import { LoanUsage } from '@/UserInput/LoanUsage';
import { PurchasePrice } from '@/UserInput/PurchasePrice';
import { TradeIn } from '@/UserInput/TradeIn';
import { VehicleAssetType } from '@/UserInput/VehicleAssetType';
import { VehicleCondition } from '@/UserInput/VehicleCondition';
import { VehicleKnown } from '@/UserInput/VehicleKnown';

import { NoLenderHomeButtons } from './NoLenderHomeButtons';

export const testId = 'Loan';

export function NoLenderHome(): JSX.Element {
    // ***** CONSTANTS *****
    // Was 'screen-loanDetails', the same id the Loan screen uses, which made the
    // NO_LENDER entry screen and the loan screen one screen in the funnel. Also
    // separates this screen's fields from Loan's, since field names come from
    // element ids.
    const baseId = 'screen-noLenderHome';
    const previousDisplay = useAppSelector(selectPreviousDisplay);
    const hideVehicleScreen = useAppSelector(selectHideVehicleScreen);
    const isAssetKnownViaParams = useAppSelector(selectIsAssetKnownViaParams);
    const isAssetTypeKnownViaParams = useAppSelector(
        selectIsAssetTypeKnownViaParams
    );
    const { userFlow } = useAppSelector(selectPartner);
    const wasPreviousDisplayIndicativelyApproved =
        previousDisplay === EDisplays.INDICATIVELY_APPROVED;
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);

    function showDoYouKnowTheVehicle(): boolean {
        if (wasPreviousDisplayIndicativelyApproved) return false;

        if (isAssetKnownViaParams) {
            if (!hideVehicleScreen) {
                return true;
            }
            return false;
        }
        return true;
    }

    return (
        <DisplayComponent baseId={baseId} data-testid={testId}>
            <Header />
            <Slogan />
            <PurchasePrice showErrors={false} baseId={baseId} />
            <LoanTerm baseId={baseId} />

            {userFlow !== EUserFlow.NO_LENDER && (
                <LoanUsage showErrors={false} baseId={baseId} />
            )}
            {userFlow === EUserFlow.NO_LENDER && (
                <BusinessFinanceType showErrors={false} baseId={baseId} />
            )}
            {!isAssetTypeKnownViaParams && (
                <VehicleAssetType showErrors={false} baseId={baseId} />
            )}

            <MultipleUserInput>
                <TradeIn showErrors={false} baseId={baseId} />
                <Deposit showErrors={false} baseId={baseId} />
            </MultipleUserInput>
            <MultipleUserInput>
                <Balloon showErrors={false} baseId={baseId} />
                <div className='w-full' />
            </MultipleUserInput>
            {showDoYouKnowTheVehicle() && (
                <VehicleKnown showErrors={false} baseId={baseId} />
            )}
            {/* showDoYouKnowTheVehicle() is fixed for the session; only the
                answer to it animates. */}
            {showDoYouKnowTheVehicle() && (
                <RevealSlot open={!!isVehicleKnown}>
                    <VehicleCondition showErrors={false} baseId={baseId} />
                </RevealSlot>
            )}
            <NoLenderHomeButtons baseId={baseId} />
        </DisplayComponent>
    );
}
